import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {Bindings} from "data/constants/bindings";
import {groupBy, take, orderBy} from 'lodash';
import type {IConferencesStore} from "data/stores/conferences/conferences.store";
import type {IStandingsStore} from "data/stores/standings/standings.store";

export interface ISquadWithResults extends ISquad {
	squadId?: number;
	wins?: number;
	ties?: number;
	losses?: number;
	played?: number;
	percentage?: number;
	conferenceRank?: number | null;
	roundId?: number;
	pointsFor?: number;
	pointsAgainst?: number;
	conferenceWins?: number;
	conferenceTies?: number;
	conferenceLosses?: number;
	divisionWins?: number;
	divisionTies?: number;
	divisionLosses?: number;
	divisionRank?: number;
}

export interface ISquad {
	id: number;
	name: string;
	shortName: string;
	abbreviation: string;
	conferenceId: number;
	divisionId: number;
	feedId: number;
}

export interface ISquadsStore {
	get getIsLoading(): boolean;
	get list(): ISquad[];
	get divisions(): Record<number, ISquad[]>;
	get nfcTopSquads(): ISquad[];
	get afcTopSquads(): ISquad[];

	getSquadById(squadId: number): ISquad | undefined;
	fetchSquads(): Promise<void>;
}

@injectable()
export class SquadsStore implements ISquadsStore {
	@observable private _isLoading: boolean = false;
	@observable private _list: ISquad[] = [];

	get list() {
		return this._list;
	}

	get divisions(): Record<string, ISquad[]> {
		return groupBy(this._list, "divisionId");
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	getSquadById(squadId: number): ISquad | undefined {
		return this._list.find((squad) => squad.id === squadId);
	}

	get nfcTopSquads() {
		return take(
			orderBy(
				this._list
					.filter((squad) => squad.conferenceId === this._conferenceStore.nfcConferenceId)
					.map((squad) => {
						const squadStanding = this._standingsStore.standingsBySquadId(squad?.id ?? 0);

						return {
							...squad,
							...squadStanding,
						} as ISquadWithResults;
					}),
				["conferenceRank"],
				["asc"]
			),
			7
		);
	}

	get afcTopSquads() {
		return take(
			orderBy(
				this._list
					.filter((squad) => squad.conferenceId === this._conferenceStore.afcConferenceId)
					.map((squad) => {
						const squadStanding = this._standingsStore.standingsBySquadId(squad?.id ?? 0);

						return {
							...squad,
							...squadStanding,
						} as ISquadWithResults;
					}),
				["conferenceRank"],
				["asc"]
			),
			7
		);
	}

	constructor(
		@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider,
		@inject(Bindings.ConferencesStore) private _conferenceStore: IConferencesStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore
	) {
		makeAutoObservable(this);
	}

	@action
	async fetchSquads() {
		const {data} = await this._jsonProvider.squads();

		runInAction(() => {
			this._list = data.items;
		});
	}
}
